import React from 'react';
import { Icon } from '@iconify-icon/react';
import Link from 'next/link';

import PageLink from '@/utils/PageLink';

const pillars = [
  {
    icon: 'tabler:building-bank',
    title: 'Sealed AWS environment',
    body: 'Frontier models run through AWS Bedrock in-region. Your prompts and files are processed there — model providers never see your traffic.',
  },
  {
    icon: 'tabler:database-off',
    title: 'Never used for training',
    body: 'Your institutional knowledge is not shared with model makers and is not used to train their models. That commitment is contractual, not a settings toggle.',
  },
  {
    icon: 'tabler:lock-access',
    title: 'You control who sees what',
    body: 'Role-based access, SSO, and domain restriction keep the Company Brain inside your walls. Export or delete data whenever you choose.',
  },
];

const SafeByDesign: React.FC = () => (
  <section
    aria-label="How Chat.co keeps company knowledge safe"
    className="bg-white pb-20 sm:pb-24">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="relative overflow-hidden rounded-[28px] bg-[#04182e] px-6 py-14 sm:px-12">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff07_1px,transparent_1px),linear-gradient(to_bottom,#ffffff07_1px,transparent_1px)] bg-[size:3rem_3rem]" />
          <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-primary-600/30 blur-[90px]" />
          <div className="absolute -bottom-24 -left-16 h-64 w-64 rounded-full bg-[#d3a641]/10 blur-[90px]" />
        </div>

        <div className="relative">
          <div className="max-w-2xl">
            <span className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#ddb85a]/90">
              Built for confidential knowledge
            </span>
            <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
              Your Company Brain stays{' '}
              <span className="font-display font-medium italic text-[#d3a641]">yours.</span>
            </h2>
            <p className="mt-4 text-base leading-relaxed text-blue-200/70">
              Connecting institutional knowledge should not mean pasting it into a consumer chatbot.
              Chat.co gives your whole company access &mdash; under privacy terms built for
              confidential work.
            </p>
          </div>

          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {pillars.map((pillar) => (
              <div
                key={pillar.title}
                className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-sm transition-colors duration-300 hover:border-[#d3a641]/30 hover:bg-white/[0.07]">
                <Icon icon={pillar.icon} className="text-3xl text-[#d3a641]" />
                <h3 className="mt-4 text-base font-bold text-white">{pillar.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-blue-200/60">{pillar.body}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 flex flex-col gap-4 border-t border-white/10 pt-8 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-blue-200/70">
              Want the full story on how we protect conversations and documents?
            </p>
            <Link
              href={PageLink.privacyPage}
              className="inline-flex items-center gap-2 rounded-full border border-white/25 px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-white hover:text-[#04182e]">
              Read how privacy works at Chat.co
              <Icon icon="tabler:arrow-right" className="text-base" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default SafeByDesign;
